import { useMemo } from "react";
import { useAuth, type AuthUser } from "../../hooks/useAuth";
import { useClaws } from "../../hooks/useClaws";
import { usePendingInstance } from "../../hooks/useOnboarding";
import { adaptInstance, adaptOwner } from "./dashboardAdapters";
import type { DashboardInstance, DashboardOwner } from "./mock";

export type DashboardStatus = "loading" | "ready" | "error";

export type DashboardData = {
  status: DashboardStatus;
  user: AuthUser | null;
  owner: DashboardOwner | null;
  instances: ReadonlyArray<DashboardInstance>;
  hasPending: boolean;
};

export const useDashboardData = (): DashboardData => {
  const { status: authStatus, user } = useAuth();
  const { status: clawsStatus, claws } = useClaws();
  const { status: pendingStatus, pending } = usePendingInstance();

  const status: DashboardStatus = useMemo(() => {
    if (authStatus === "loading") return "loading";
    if (clawsStatus === "loading" || pendingStatus === "loading") return "loading";
    if (clawsStatus === "error") return "error";
    return "ready";
  }, [authStatus, clawsStatus, pendingStatus]);

  const owner: DashboardOwner | null = useMemo(
    () => (user ? adaptOwner(user) : null),
    [user],
  );

  // Locked tenants live only in `pending`. We deliberately do NOT inject
  // them into the switcher / hero — otherwise the admin "Unlock" gate is
  // a no-op for the user. DashboardOverview detects an empty `instances`
  // list and renders DashboardEmpty, which uses the separate `pending`
  // prop to surface a "we're setting up your instance" card.
  const instances: ReadonlyArray<DashboardInstance> = useMemo(
    () => claws.map(adaptInstance),
    [claws],
  );

  return {
    status,
    user,
    owner,
    instances,
    hasPending: pending !== null,
  };
};
